import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Tabs, Tag, Typography
} from 'antd';
import { SaveOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { contraVoucherApi } from './contraVoucherApi';
import {
  STATUS_COLOR, STATUS_LABEL,
  TRANSFER_TYPES, classifyCashOrBank, inferTransferType,
  findContraFormErrors, buildCreatePayload, friendlyErrorFor
} from './contraVoucherHelpers';
import AuditLogPanel from '../AuditLogPanel';
import AttachmentsPanel from '../AttachmentsPanel';
import '../accounts.css';

const { Text } = Typography;

const VIEW_MODES = new Set(['view']);
const READONLY_STATUSES = new Set(['posted', 'cancelled']);

// ─────────────────────────────────────────────────────────────────
// Glass-i — Contra Voucher Add / Edit / View modal (Stage 6B.9C).
//
// Centered modal (920-wide). Smaller than the Journal modal because
// Contra has fewer fields (just From, To, Amount, Date + reference
// extras). Posting + cancellation are NOT in this component — the
// parent ContraVoucherPage owns the Approval-MFA flow.
// ─────────────────────────────────────────────────────────────────

function emptyForm() {
  return {
    voucherDate:     dayjs(),
    transferType:    null,
    fromAccountId:   null,
    toAccountId:     null,
    amount:          null,
    referenceNo:     '',
    chequeNo:        '',
    bankReferenceNo: '',
    narration:       '',
    remarks:         ''
  };
}

function defaultsFor(mode, record) {
  if (mode === 'add' || !record) return emptyForm();
  return {
    voucherDate:     record.voucherDate ? dayjs(record.voucherDate) : dayjs(),
    transferType:    null,        // derived from the loaded ledgers on display
    fromAccountId:   record.fromAccountId,
    toAccountId:     record.toAccountId,
    amount:          Number(record.amount) || 0,
    referenceNo:     '',
    chequeNo:        '',
    bankReferenceNo: '',
    narration:       record.narration || '',
    remarks:         ''
  };
}

// Cash/Bank ledger picker — searchable, filterable by hint
// ('cash' | 'bank' | null). When the transfer type pre-selects a
// hint, the dropdown only shows matching rows.
function CashBankSelect({
  value, onChange, disabled, lookupCache, exclude, hint, placeholder
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  // Seed the option list with the currently-selected ledger if any.
  useEffect(() => {
    if (value && lookupCache && lookupCache.has(value)) {
      const row = lookupCache.get(value);
      setOptions((prev) => {
        if (prev.find((p) => p.id === value)) return prev;
        return [{ ...row }, ...prev];
      });
    }
  }, [value, lookupCache]);

  const fetchOptions = useCallback(async (q) => {
    setLoading(true);
    try {
      const rows = await contraVoucherApi.cashBankLookup(q || '', 100);
      if (lookupCache) for (const r of rows) lookupCache.set(r.id, r);
      setOptions(rows);
    } catch (_e) {
      // swallow — empty list will show the "No accounts" message.
    } finally { setLoading(false); }
  }, [lookupCache]);

  const onOpen = useCallback((open) => {
    if (open && options.length === 0) fetchOptions('');
  }, [options.length, fetchOptions]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(text || ''), 200);
  }, [fetchOptions]);

  // Apply two filters in JS: hint (cash/bank) + exclude-this-id.
  const filtered = useMemo(() => {
    return options.filter((o) => {
      if (exclude && o.id === exclude) return false;
      if (hint) {
        const kind = classifyCashOrBank(o);
        if (kind !== hint) return false;
      }
      return true;
    });
  }, [options, hint, exclude]);

  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder={placeholder || 'Search Cash / Bank ledger…'}
      value={value || undefined}
      onChange={(v) => onChange(v)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      notFoundContent={loading ? 'Loading…' : (hint
        ? `No ${hint === 'bank' ? 'Bank' : 'Cash'} accounts match — clear the transfer type to see all.`
        : 'No Cash or Bank ledgers configured. Add one under Accounts → Ledgers.')}
      options={filtered.map((o) => {
        const kind = classifyCashOrBank(o);
        return {
          value: o.id,
          label: (
            <span>
              <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
              {o.accountName}
              {kind ? (
                <Tag style={{ marginLeft: 6 }} color={kind === 'bank' ? 'blue' : 'green'}>
                  {kind === 'bank' ? 'Bank' : 'Cash'}
                </Tag>
              ) : null}
            </span>
          )
        };
      })}
    />
  );
}

function fmtRupee(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ContraVoucherModal({
  open, mode, record, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [formState, setFormState] = useState(emptyForm());
  const lookupCacheRef = useRef(new Map());

  const isView   = VIEW_MODES.has(mode);
  const isLocked = record && READONLY_STATUSES.has(record.status);
  const readOnly = isView || isLocked;
  const status   = record ? record.status : 'draft';

  // Seed the cash/bank lookup cache so account cells in view/edit
  // render their account name immediately.
  useEffect(() => {
    if (!open) return;
    contraVoucherApi.cashBankLookup('', 200).then((rows) => {
      for (const r of rows) lookupCacheRef.current.set(r.id, r);
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const init = defaultsFor(mode, record);
    setFormState(init);
    form.setFieldsValue({
      voucherDate:     init.voucherDate,
      amount:          init.amount,
      referenceNo:     init.referenceNo,
      chequeNo:        init.chequeNo,
      bankReferenceNo: init.bankReferenceNo,
      narration:       init.narration,
      remarks:         init.remarks
    });
  }, [open, mode, record, form]);

  const patch = useCallback((delta) => {
    setFormState((prev) => ({ ...prev, ...delta }));
  }, []);

  // Derive the cash/bank hint per field from the chosen transfer type.
  const fromHint = useMemo(() => {
    const t = TRANSFER_TYPES.find((x) => x.key === formState.transferType);
    return t ? t.fromHint : null;
  }, [formState.transferType]);
  const toHint = useMemo(() => {
    const t = TRANSFER_TYPES.find((x) => x.key === formState.transferType);
    return t ? t.toHint : null;
  }, [formState.transferType]);

  // Detect "type mismatch" — operator picked Cash Deposit but the
  // From account is a bank ledger, etc.
  const mismatchWarning = useMemo(() => {
    const cache = lookupCacheRef.current;
    if (!formState.transferType || !formState.fromAccountId || !formState.toAccountId) return null;
    const from = cache.get(formState.fromAccountId);
    const to   = cache.get(formState.toAccountId);
    if (!from || !to) return null;
    const inferred = inferTransferType(from, to);
    if (inferred && inferred !== formState.transferType) {
      const wanted = TRANSFER_TYPES.find((t) => t.key === formState.transferType);
      const got    = TRANSFER_TYPES.find((t) => t.key === inferred);
      return `You picked "${wanted ? wanted.label : formState.transferType}" but the chosen accounts describe a "${got ? got.label : inferred}".`;
    }
    return null;
  }, [formState.transferType, formState.fromAccountId, formState.toAccountId]);

  const previewLines = useMemo(() => {
    const cache = lookupCacheRef.current;
    const debit  = cache.get(formState.toAccountId);
    const credit = cache.get(formState.fromAccountId);
    const amt = Number(formState.amount) || 0;
    return {
      debit:  debit  ? `${debit.accountCode} — ${debit.accountName}`   : null,
      credit: credit ? `${credit.accountCode} — ${credit.accountName}` : null,
      amount: amt
    };
  }, [formState.toAccountId, formState.fromAccountId, formState.amount]);

  const buildPayloadFromForm = useCallback(async () => {
    const headerValues = await form.validateFields();
    return buildCreatePayload({
      voucherDate:     headerValues.voucherDate ? headerValues.voucherDate.format('YYYY-MM-DD') : null,
      fromAccountId:   formState.fromAccountId,
      toAccountId:     formState.toAccountId,
      amount:          headerValues.amount,
      referenceNo:     headerValues.referenceNo,
      chequeNo:        headerValues.chequeNo,
      bankReferenceNo: headerValues.bankReferenceNo,
      narration:       headerValues.narration,
      remarks:         headerValues.remarks
    });
  }, [form, formState.fromAccountId, formState.toAccountId]);

  const saveDraft = useCallback(async () => {
    // Collect both form-validated values + state-only fields
    let headerValues;
    try {
      headerValues = await form.validateFields();
    } catch {
      return;  // antD already highlights bad fields
    }
    const probe = {
      voucherDate:     headerValues.voucherDate ? headerValues.voucherDate.format('YYYY-MM-DD') : null,
      fromAccountId:   formState.fromAccountId,
      toAccountId:     formState.toAccountId,
      amount:          headerValues.amount,
      referenceNo:     headerValues.referenceNo,
      chequeNo:        headerValues.chequeNo,
      bankReferenceNo: headerValues.bankReferenceNo,
      narration:       headerValues.narration,
      remarks:         headerValues.remarks
    };
    const errs = findContraFormErrors(probe);
    if (errs.length) {
      Modal.warning({
        title: 'Please fix these issues',
        content: <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13 }}>{errs.join('\n')}</pre>,
        okText: 'Close'
      });
      return;
    }

    setBusy(true);
    try {
      const payload = await buildPayloadFromForm();
      let saved;
      if (mode === 'add') {
        saved = await contraVoucherApi.createDraft(payload);
        message.success(`Contra draft ${saved.voucherNo} saved.`);
      } else if (mode === 'edit') {
        saved = await contraVoucherApi.updateDraft(record.id, payload);
        message.success('Contra draft updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [form, formState, mode, record, onSaved, buildPayloadFromForm]);

  const title = useMemo(() => {
    if (mode === 'add')  return 'New Contra Voucher';
    const vn = record && record.voucherNo ? ' — ' + record.voucherNo : '';
    if (mode === 'view') return `View Contra Voucher${vn}`;
    return `Edit Contra Voucher${vn}`;
  }, [mode, record]);

  const footer = (
    <Space>
      {!readOnly ? (
        <Button type="primary" icon={<SaveOutlined />} loading={busy} onClick={saveDraft}>
          {mode === 'add' ? 'Save Draft' : 'Save Changes'}
        </Button>
      ) : null}
      <Button onClick={onClose}>Close</Button>
    </Space>
  );

  // Stage UI-FIX (Contra Voucher focus loss): `function DetailsBody()`
  // was declared inside this parent component's body — every parent
  // re-render produced a brand new function identity → React treated
  // `<DetailsBody />` as a different component type each render and
  // remounted the entire form, dropping focus on every keystroke.
  // Replacing with a JSX const keeps element types stable so React
  // reconciles in-place. Same closures (form / formState / patch /
  // readOnly / previewLines), same validation, same posting preview.
  const detailsBody = (<>

      <Form className="erp-accounts-form" form={form} layout="vertical" preserve>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Voucher No">
              <Input
                value={record?.voucherNo || (mode === 'add' ? '(auto-generated)' : '')}
                disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Voucher Date" name="voucherDate"
              rules={[{ required: true, message: 'Voucher date is required' }]}>
              <DatePicker style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Transfer Type" extra="Helps pre-filter the account pickers.">
              <Select
                allowClear
                disabled={readOnly}
                placeholder="Pick a preset"
                value={formState.transferType || undefined}
                onChange={(v) => patch({ transferType: v || null })}
                options={TRANSFER_TYPES.map((t) => ({
                  value: t.key, label: t.label
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="From Account" required
              extra="Money leaves this account (credited).">
              <CashBankSelect
                value={formState.fromAccountId}
                onChange={(v) => patch({ fromAccountId: v })}
                disabled={readOnly}
                lookupCache={lookupCacheRef.current}
                exclude={formState.toAccountId}
                hint={fromHint}
                placeholder={fromHint
                  ? `Pick a ${fromHint === 'bank' ? 'Bank' : 'Cash'} ledger…`
                  : 'Pick a Cash or Bank ledger…'}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="To Account" required
              extra="Money lands in this account (debited).">
              <CashBankSelect
                value={formState.toAccountId}
                onChange={(v) => patch({ toAccountId: v })}
                disabled={readOnly}
                lookupCache={lookupCacheRef.current}
                exclude={formState.fromAccountId}
                hint={toHint}
                placeholder={toHint
                  ? `Pick a ${toHint === 'bank' ? 'Bank' : 'Cash'} ledger…`
                  : 'Pick a Cash or Bank ledger…'}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Amount (₹)" name="amount"
              rules={[
                { required: true, message: 'Amount is required.' },
                () => ({
                  validator(_, v) {
                    if (v == null || v === '') return Promise.resolve();
                    if (Number(v) > 0) return Promise.resolve();
                    return Promise.reject(new Error('Amount must be greater than zero.'));
                  }
                })
              ]}>
              <InputNumber
                disabled={readOnly}
                style={{ width: '100%' }}
                min={0} step={1} precision={2}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Reference No." name="referenceNo">
              <Input maxLength={100} disabled={readOnly}
                placeholder="Slip / receipt / journal ref" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Cheque No." name="chequeNo">
              <Input maxLength={40} disabled={readOnly}
                placeholder="Optional (cheque deposits)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="UTR / Bank Reference No." name="bankReferenceNo">
              <Input maxLength={100} disabled={readOnly}
                placeholder="Optional (NEFT / IMPS / RTGS)" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Narration" name="narration">
              <Input maxLength={2000} disabled={readOnly}
                placeholder="Short description of the transfer" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea
                rows={2} maxLength={2000} disabled={readOnly}
                placeholder="Internal notes (optional)" />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Mismatch warning between picked Transfer Type and chosen accounts. */}
      {mismatchWarning ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Transfer type doesn't match chosen accounts"
          description={mismatchWarning} />
      ) : null}

      {/* Posting preview — only when we have both ledgers + amount. */}
      {previewLines.debit && previewLines.credit && previewLines.amount > 0 ? (
        <div className="erp-accounts-posting-preview"
          style={{ background: '#f5f9ff', border: '1px solid #d6e4ff',
                   borderRadius: 6, padding: '10px 14px', marginTop: 6 }}>
          <Text strong>Posting preview</Text>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            <div>
              <Text type="secondary">Debit:</Text>
              <Text strong style={{ marginLeft: 8 }}>{previewLines.debit}</Text>
              <Text strong style={{ float: 'right' }}>₹ {fmtRupee(previewLines.amount)}</Text>
            </div>
            <div>
              <Text type="secondary">Credit:</Text>
              <Text strong style={{ marginLeft: 8 }}>{previewLines.credit}</Text>
              <Text strong style={{ float: 'right' }}>₹ {fmtRupee(previewLines.amount)}</Text>
            </div>
          </div>
        </div>
      ) : null}

      {status === 'cancelled' && record && record.cancelReason ? (
        <Alert type="error" showIcon style={{ marginTop: 12 }}
          message="Cancelled voucher"
          description={<><b>Reason:</b> {record.cancelReason}</>} />
      ) : null}
    </>);

  return (
    <Modal
      title={
        <span>
          <SwapOutlined style={{ marginRight: 8, color: '#1677ff' }} />
          {title}
          {record && record.status ? (
            <Tag color={STATUS_COLOR[record.status] || 'default'}
                 style={{ marginInlineStart: 12 }}>
              {STATUS_LABEL[record.status] || record.status}
            </Tag>
          ) : null}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={920}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      {mode === 'view' && record && record.id ? (
        <Tabs size="small" defaultActiveKey="details" items={[
          { key: 'details', label: 'Details', children: detailsBody },
          { key: 'audit',   label: 'Audit', children: (
            <AuditLogPanel entityType="accounting_voucher" entityId={record.id} />
          ) },
          { key: 'attachments', label: 'Attachments', children: (
            <AttachmentsPanel
              entityType="accounting_voucher" entityId={record.id}
              canUpload canDelete />
          ) }
        ]} />
      ) : (
        detailsBody
      )}
    </Modal>
  );
}
